if (!customElements.get('media-gallery')) {
  function updatePdpStickyOffset() {
    const headerSections = document.querySelectorAll('.shopify-section-group-header-group');
    let bottom = 0;
    headerSections.forEach((section) => {
      bottom = Math.max(bottom, section.getBoundingClientRect().bottom);
    });
    document.documentElement.style.setProperty('--pdp-sticky-offset', `${Math.max(bottom, 0)}px`);
  }
  updatePdpStickyOffset();
  window.addEventListener('resize', debounce(updatePdpStickyOffset, 100));
  window.addEventListener('scroll', throttle(updatePdpStickyOffset, 100), { passive: true });

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.product__media-dots'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.elements.thumbnails.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest('.slider-button--prev, .slider-button--next');
            if (!button) return;
            event.stopImmediatePropagation();
            event.preventDefault();
            this.stepActiveThumbnail(button.classList.contains('slider-button--prev') ? -1 : 1);
          },
          { capture: true }
        );
        this.elements.thumbnails.querySelectorAll('.slider-button--prev, .slider-button--next').forEach((button) => {
          new MutationObserver(() => button.removeAttribute('disabled')).observe(button, {
            attributes: true,
            attributeFilter: ['disabled'],
          });
        });
        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('[data-target]').forEach((dot) => {
            dot.addEventListener('click', this.setActiveMedia.bind(this, dot.dataset.target, false));
          });

          const dotsBar = this.elements.dots.closest('.slider-buttons');
          if (dotsBar) {
            const prevButton = dotsBar.querySelector('.slider-button--prev');
            const nextButton = dotsBar.querySelector('.slider-button--next');
            if (prevButton) prevButton.addEventListener('click', this.stepActiveDot.bind(this, -1));
            if (nextButton) nextButton.addEventListener('click', this.stepActiveDot.bind(this, 1));
          }

          const initialActiveDot = this.elements.dots.querySelector('.is-active');
          if (initialActiveDot) initialActiveDot.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        }
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (this.mql.matches) return;
        const mediaId = event.detail.currentElement.dataset.mediaId;
        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(thumbnail);
        this.setActiveDot(this.elements.dots && this.elements.dots.querySelector(`[data-target="${mediaId}"]`));
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.setActiveDot(this.elements.dots && this.elements.dots.querySelector(`[data-target="${mediaId}"]`));
        if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.mql.matches) {
          thumbnail.scrollIntoView({ block: 'nearest' });
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveDot(dot) {
        if (!this.elements.dots) return;
        this.elements.dots.querySelectorAll('[data-target]').forEach((element) => element.classList.remove('is-active'));
        if (!dot) return;
        dot.classList.add('is-active');
        dot.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }

      getActiveMediaTarget() {
        const activeMedia = this.elements.viewer.querySelector('[data-media-id].is-active');
        return activeMedia && activeMedia.dataset.mediaId;
      }

      stepActiveDot(direction) {
        const dots = Array.from(this.elements.dots.querySelectorAll('[data-target]'));
        const activeTarget = this.getActiveMediaTarget();
        const currentIndex = dots.findIndex((dot) => dot.dataset.target === activeTarget);
        const nextIndex = (currentIndex + direction + dots.length) % dots.length;
        this.setActiveMedia(dots[nextIndex].dataset.target, false);
      }

      stepActiveThumbnail(direction) {
        const thumbnails = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
        const activeTarget = this.getActiveMediaTarget();
        const currentIndex = thumbnails.findIndex((thumbnail) => thumbnail.dataset.target === activeTarget);
        const nextIndex = (currentIndex + direction + thumbnails.length) % thumbnails.length;
        this.setActiveMedia(thumbnails[nextIndex].dataset.target, false);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
